import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Sparkles, KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = params.get("token");
    if (t) setToken(t);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Reset token is required");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset. You can now sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md p-8 rounded-2xl border border-border bg-card shadow-xl shadow-primary/5" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl kn-gradient-bg flex items-center justify-center shadow-lg shadow-primary/30">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <p className="font-display text-base font-semibold">KARIS</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Reset your password</p>
          </div>
        </div>

        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
            <h2 className="font-display text-2xl font-semibold mt-3">Password reset</h2>
            <p className="text-sm text-muted-foreground mt-1">Taking you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Set a new password</h1>
            <p className="text-sm text-muted-foreground mt-1">Enter the token from the reset link sent to your email.</p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em]">Reset token</Label>
                <Input
                  data-testid="reset-token-input"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste the token from your email"
                  required
                  className="h-11 font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em]">New password</Label>
                <Input data-testid="reset-new-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em]">Confirm password</Label>
                <Input data-testid="reset-confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="h-11" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-11 kn-gradient-bg text-white border-0 hover:opacity-95">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting…</> : "Reset password"}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-xs text-primary hover:underline"><Sparkles className="inline w-3 h-3 mr-1" /> Back to sign in</Link>
              </div>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
